import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { buildGithubLoginUrl } from '@/lib/auth-routing';
import { getAuthSession, logoutAuthSession } from '@/lib/api';
import type { CurrentUser } from '@/lib/api';

const SESSION_QUERY_KEY = ['authSession'] as const;

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export function deriveSessionState(data: CurrentUser | null | undefined): {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
} {
  const user = data ?? null;

  return {
    user,
    isAuthenticated: user !== null,
    isAdmin: user?.role === 'admin',
  };
}

export function resolveSessionStatus(input: {
  data: CurrentUser | null | undefined;
  isLoading: boolean;
  isError: boolean;
}): SessionStatus {
  if (input.isLoading) {
    return 'loading';
  }

  if (input.data !== undefined) {
    return input.data === null ? 'unauthenticated' : 'authenticated';
  }

  return input.isError ? 'error' : 'loading';
}

export function useSession(): {
  user: Awaited<ReturnType<typeof getAuthSession>>;
  status: SessionStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasError: boolean;
  startLogin: (returnTo?: string) => void;
  logout: () => Promise<void>;
} {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: getAuthSession,
    retry: false,
    staleTime: 60_000,
  });

  const startLogin = useCallback((returnTo?: string): void => {
    window.location.replace(buildGithubLoginUrl(undefined, undefined, returnTo));
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutAuthSession();
    } finally {
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  }, [queryClient]);

  const sessionState = deriveSessionState(sessionQuery.data);
  const status = resolveSessionStatus({
    data: sessionQuery.data,
    isLoading: sessionQuery.isLoading,
    isError: sessionQuery.isError,
  });

  return {
    user: sessionState.user,
    status,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
    isAdmin: sessionState.isAdmin,
    hasError: status === 'error',
    startLogin,
    logout,
  };
}
